import React, { useRef, useEffect, useMemo } from 'react';
import { ButtonSelect, Block, Row, IconName, Button, Icon, Flex } from '@upsales/components';
import { useDetectContainerOverflow } from '../hooks';
import BemClass from '@upsales/components/Utils/bemClass';
import './HistoryLogFilterButtons.scss';

type ButtonConfig<TType extends string> = { title: string; value: TType; icon?: IconName };

type SingleSelectProps<TType extends string> = {
	multiSelect?: false;
	historyType: TType;
	setHistoryType: StateSetter<TType>;
	selectedFilters?: never;
	onFilterChange?: never;
};

type MultiSelectProps<TType extends string> = {
	multiSelect: true;
	selectedFilters: TType[];
	onFilterChange: (value: TType) => void;
	historyType?: never;
	setHistoryType?: never;
};

type Props<TType extends string> = {
	containerRef: React.RefObject<HTMLDivElement>;
	buttons: readonly ButtonConfig<TType>[];
	metadata: { [k in TType]?: number };
} & (SingleSelectProps<TType> | MultiSelectProps<TType>);

const HistoryLogFilterButtons = <TType extends string>(props: Props<TType>) => {
	const { containerRef, metadata, buttons, multiSelect } = props;

	const buttonSelectRef = useRef<HTMLDivElement>(null);
	const classes = new BemClass('HistoryLogFilterButtons');

	const { thresholdMet: isSmall, recalculate } = useDetectContainerOverflow({
		containerRef,
		contentRefs: [buttonSelectRef],
		threshold: 240
	});

	useEffect(() => {
		recalculate();
	}, [metadata]);

	const filteredButtons = useMemo(
		() =>
			buttons.flatMap(obj => {
				if (!metadata[obj.value]) return [];
				return [
					{
						...obj,
						title: `${isSmall && !(obj.value === 'all') ? '' : obj.title} (${metadata[obj.value]})`
					}
				];
			}),
		[buttons, metadata, isSmall]
	);

	// Multi-select mode
	if (multiSelect) {
		const { selectedFilters, onFilterChange } = props;

		return (
			<Row ref={buttonSelectRef} noWrap>
				<Block className="HistoryLog__filter-select" space="mlm">
					<Flex gap="u1" wrap="wrap" className={classes.b()}>
						{filteredButtons.map(button => {
							const isSelected = selectedFilters.includes(button.value);
							return (
								<Button
									key={button.value}
									size="sm"
									type={isSelected ? undefined : 'lined'}
									color={isSelected ? 'bright-blue' : 'grey'}
									onClick={() => onFilterChange(button.value)}
									className={classes.elem('button').mod({ selected: isSelected }).b()}
								>
									{button.icon ? <Icon name={button.icon} space="mrs" /> : null}
									{button.title}
								</Button>
							);
						})}
					</Flex>
				</Block>
			</Row>
		);
	}

	// Single-select mode (backward compatible)
	const { historyType, setHistoryType } = props;

	return (
		<Row ref={buttonSelectRef} noWrap>
			<Block className="HistoryLog__filter-select" space="mlm">
				<ButtonSelect size="sm" value={historyType} onChange={setHistoryType} options={filteredButtons} />
			</Block>
		</Row>
	);
};

export default HistoryLogFilterButtons;
